import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";

import {
  fetchQualityNcrDeviceWork,
  qualityNcrDeviceWorkQueryKey,
  saveQualityNcrDeviceWork,
} from "./qualityEventNcrApi";
import { QualityNcrDevicePhotos } from "./QualityNcrDevicePhotos";
import { presentNcrDeviceWork, type RcaDeviceWork, type RcaWindowRow } from "./rcaForm";

const inputClass =
  "w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900";

/**
 * Loads, presents, and saves the device investigation worksheet stored on one NCR.
 * `seed` is the legacy quality.rca.form.devices entry used until the NCR row is saved once.
 */
export function QualityNcrDeviceWorkPanel({
  ncrId,
  seed,
  writesAllowed,
  onSaved,
}: {
  ncrId: number;
  seed: RcaDeviceWork | null;
  writesAllowed: boolean;
  onSaved?: (work: RcaDeviceWork) => void;
}) {
  const queryClient = useQueryClient();
  const workQuery = useQuery({
    queryKey: qualityNcrDeviceWorkQueryKey(ncrId),
    queryFn: () => fetchQualityNcrDeviceWork(ncrId),
  });
  const [work, setWork] = useState<RcaDeviceWork | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    if (workQuery.data != null) {
      setWork(presentNcrDeviceWork(workQuery.data.work, seed));
    }
  }, [workQuery.data, seed]);

  const save = useMutation({
    mutationFn: (next: RcaDeviceWork) => saveQualityNcrDeviceWork(ncrId, next),
    onSuccess: (saved, next) => {
      setSaveError(null);
      queryClient.setQueryData(qualityNcrDeviceWorkQueryKey(ncrId), saved);
      onSaved?.(next);
    },
    onError: (error: Error) => setSaveError(error.message || "Could not save the device worksheet."),
  });

  if (workQuery.isLoading || work == null) {
    return <p className="text-sm text-slate-600">Loading device worksheet.</p>;
  }
  if (workQuery.isError) {
    return <p className="text-sm text-red-700">{(workQuery.error as Error).message}</p>;
  }
  return (
    <div className="space-y-4">
      {saveError ? <p className="text-sm text-red-700">{saveError}</p> : null}
      <QualityNcrDeviceWorkFields
        ncrId={ncrId}
        work={work}
        readOnly={!writesAllowed}
        onChange={setWork}
        onSave={(next) => {
          if (!writesAllowed) return;
          setWork(next);
          save.mutate(next);
        }}
      />
    </div>
  );
}

export function QualityNcrDeviceWorkFields({
  ncrId,
  work,
  readOnly,
  onChange,
  onSave,
}: {
  ncrId: number;
  work: RcaDeviceWork;
  readOnly: boolean;
  onChange: (next: RcaDeviceWork) => void;
  onSave: (next: RcaDeviceWork) => void;
}) {
  const setBattery = (key: keyof RcaDeviceWork["battery"], value: string) => {
    onChange({ ...work, battery: { ...work.battery, [key]: value } });
  };
  const saveBattery = (key: keyof RcaDeviceWork["battery"], value: string) => {
    onSave({ ...work, battery: { ...work.battery, [key]: value } });
  };
  const setDiagnostics = (key: keyof RcaDeviceWork["diagnostics"], value: string) => {
    onChange({ ...work, diagnostics: { ...work.diagnostics, [key]: value } });
  };
  const saveDiagnostics = (key: keyof RcaDeviceWork["diagnostics"], value: string) => {
    onSave({ ...work, diagnostics: { ...work.diagnostics, [key]: value } });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Field dates</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <TextField
            label="Date problems first reported"
            value={work.dateProblemsFirstReported}
            readOnly={readOnly}
            onChange={(value) => onChange({ ...work, dateProblemsFirstReported: value })}
            onBlur={(value) => onSave({ ...work, dateProblemsFirstReported: value })}
          />
          <TextField
            label="Field install date (approx)"
            value={work.fieldInstallDate}
            readOnly={readOnly}
            onChange={(value) => onChange({ ...work, fieldInstallDate: value })}
            onBlur={(value) => onSave({ ...work, fieldInstallDate: value })}
          />
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Battery details</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <TextField
            label="Battery type (brand / model / date of manufacture)"
            placeholder="Xeno / XL-145F / C / 5-2025"
            value={work.battery.type}
            readOnly={readOnly}
            onChange={(value) => setBattery("type", value)}
            onBlur={(value) => saveBattery("type", value)}
          />
          <TextField
            label="Initial battery voltages (per cell)"
            placeholder="From the battery itself"
            multiline
            value={work.battery.initialVoltages}
            readOnly={readOnly}
            onChange={(value) => setBattery("initialVoltages", value)}
            onBlur={(value) => saveBattery("initialVoltages", value)}
          />
          <TextField
            label="Battery health (batt command output)"
            placeholder="Paste the raw batt command output"
            multiline
            value={work.battery.health}
            readOnly={readOnly}
            onChange={(value) => setBattery("health", value)}
            onBlur={(value) => saveBattery("health", value)}
          />
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Initial intake diagnostics</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <TextField
            label="Visual inspection notes"
            multiline
            value={work.diagnostics.visualInspection}
            readOnly={readOnly}
            onChange={(value) => setDiagnostics("visualInspection", value)}
            onBlur={(value) => saveDiagnostics("visualInspection", value)}
          />
          <QualityNcrDevicePhotos ncrId={ncrId} readOnly={readOnly} />
          <TextField
            label="Config command dump"
            multiline
            value={work.diagnostics.configDump}
            readOnly={readOnly}
            onChange={(value) => setDiagnostics("configDump", value)}
            onBlur={(value) => saveDiagnostics("configDump", value)}
          />
          <TextField
            label="Stat command dump"
            multiline
            value={work.diagnostics.statDump}
            readOnly={readOnly}
            onChange={(value) => setDiagnostics("statDump", value)}
            onBlur={(value) => saveDiagnostics("statDump", value)}
          />
          <TextField
            label="Batt command dump (initial)"
            multiline
            value={work.diagnostics.battDump}
            readOnly={readOnly}
            onChange={(value) => setDiagnostics("battDump", value)}
            onBlur={(value) => saveDiagnostics("battDump", value)}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <TextField
              label="Device able to communicate via cell?"
              value={work.diagnostics.cellCommunicate}
              readOnly={readOnly}
              onChange={(value) => setDiagnostics("cellCommunicate", value)}
              onBlur={(value) => saveDiagnostics("cellCommunicate", value)}
            />
            <TextField
              label="Device able to communicate via serial?"
              value={work.diagnostics.serialCommunicate}
              readOnly={readOnly}
              onChange={(value) => setDiagnostics("serialCommunicate", value)}
              onBlur={(value) => saveDiagnostics("serialCommunicate", value)}
            />
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Appsmith data logs</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-600">Pull data logs for each window. Note signal strength and battery health.</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-1 font-medium">Window</th>
                  <th className="px-2 py-1 font-medium">Date range</th>
                  <th className="px-2 py-1 font-medium">Signal strength observations</th>
                  <th className="px-2 py-1 font-medium">Battery health observations</th>
                </tr>
              </thead>
              <tbody>
                {work.windows.map((row, index) => (
                  <tr key={`window-${index}`}>
                    <td className="px-2 py-1 align-top">
                      <input
                        className={inputClass}
                        value={row.window}
                        readOnly={readOnly}
                        onChange={(event) => updateWindow(work, index, { window: event.target.value }, onChange)}
                        onBlur={(event) => updateWindow(work, index, { window: event.target.value }, onSave)}
                      />
                    </td>
                    <td className="px-2 py-1 align-top">
                      <input
                        className={inputClass}
                        value={row.dateRange}
                        readOnly={readOnly}
                        onChange={(event) => updateWindow(work, index, { dateRange: event.target.value }, onChange)}
                        onBlur={(event) => updateWindow(work, index, { dateRange: event.target.value }, onSave)}
                      />
                    </td>
                    <td className="px-2 py-1 align-top">
                      <textarea
                        className={inputClass}
                        rows={2}
                        value={row.signal}
                        readOnly={readOnly}
                        onChange={(event) => updateWindow(work, index, { signal: event.target.value }, onChange)}
                        onBlur={(event) => updateWindow(work, index, { signal: event.target.value }, onSave)}
                      />
                    </td>
                    <td className="px-2 py-1 align-top">
                      <textarea
                        className={inputClass}
                        rows={2}
                        value={row.battery}
                        readOnly={readOnly}
                        onChange={(event) => updateWindow(work, index, { battery: event.target.value }, onChange)}
                        onBlur={(event) => updateWindow(work, index, { battery: event.target.value }, onSave)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {readOnly ? null : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                const next = {
                  ...work,
                  windows: [...work.windows, { window: "", dateRange: "", signal: "", battery: "" }],
                };
                onChange(next);
                onSave(next);
              }}
            >
              Add row
            </Button>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function TextField({
  label,
  value,
  placeholder,
  multiline,
  readOnly,
  onChange,
  onBlur,
}: {
  label: string;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  readOnly: boolean;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {multiline ? (
        <textarea
          className={inputClass}
          rows={4}
          placeholder={placeholder}
          value={value}
          readOnly={readOnly}
          onChange={(event) => onChange(event.target.value)}
          onBlur={(event) => onBlur(event.target.value)}
        />
      ) : (
        <input
          className={inputClass}
          placeholder={placeholder}
          value={value}
          readOnly={readOnly}
          onChange={(event) => onChange(event.target.value)}
          onBlur={(event) => onBlur(event.target.value)}
        />
      )}
    </label>
  );
}

function updateWindow(
  work: RcaDeviceWork,
  index: number,
  patch: Partial<RcaWindowRow>,
  apply: (next: RcaDeviceWork) => void,
) {
  const windows = work.windows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row));
  apply({ ...work, windows });
}
